import React, { useState, useEffect } from 'react'
import {
  Package,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  RefreshCw,
  Smile,
  Meh,
  Frown,
  Truck,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, StatsCard } from '../../components/ui'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts'
import {
  apiService,
  PostSalesDashboardMetrics,
  DeliveryTimeline,
  RecentDelivery,
} from '../../services/api'

export const PostSalesDashboardPage: React.FC = () => {
  const [metrics, setMetrics] = useState<PostSalesDashboardMetrics | null>(null)
  const [timeline, setTimeline] = useState<DeliveryTimeline[]>([])
  const [recentDeliveries, setRecentDeliveries] = useState<RecentDelivery[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const loadPostSalesData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const [metricsData, timelineData, deliveriesData] = await Promise.all([
        apiService.getPostSalesMetrics(),
        apiService.getPostSalesTimeline(6),
        apiService.getPostSalesRecentDeliveries(10),
      ])

      setMetrics(metricsData)
      setTimeline(timelineData)
      setRecentDeliveries(deliveriesData)
    } catch (err) {
      console.error('Erro ao carregar dados de pós-vendas:', err)
      setError('Não foi possível carregar os dados de pós-vendas')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    void loadPostSalesData()
  }, [])

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getNPSCategory = (score: number): { label: string; color: string } => {
    if (score >= 9) return { label: 'Promotor', color: 'text-emerald-600' }
    if (score >= 7) return { label: 'Neutro', color: 'text-yellow-600' }
    return { label: 'Detrator', color: 'text-red-600' }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => void loadPostSalesData()}
          className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent/90"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard de Pós-Vendas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Acompanhe entregas, satisfação do cliente e performance pós-venda
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => void loadPostSalesData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-accent/10 text-accent rounded-md hover:bg-accent/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Total de Entregas"
              value={metrics.totalDeliveries.toString()}
              icon={Package}
              iconColor="text-blue-600"
              iconBg="bg-blue-500/10"
            />
            <StatsCard
              title="Entregas Pendentes"
              value={metrics.pendingDeliveries.toString()}
              icon={Truck}
              iconColor="text-orange-600"
              iconBg="bg-orange-500/10"
            />
            <StatsCard
              title="Entregas deste Mês"
              value={metrics.deliveriesThisMonth.toString()}
              icon={Calendar}
              iconColor="text-emerald-600"
              iconBg="bg-emerald-500/10"
              trend={
                metrics.deliveriesTrend !== 0
                  ? { value: Math.abs(metrics.deliveriesTrend), isPositive: metrics.deliveriesTrend > 0 }
                  : undefined
              }
            />
            <StatsCard
              title="NPS Médio"
              value={metrics.averageNPS > 0 ? metrics.averageNPS.toFixed(1) : 'N/A'}
              icon={Smile}
              iconColor="text-purple-600"
              iconBg="bg-purple-500/10"
            />
          </div>

          {/* NPS Details */}
          {metrics.totalSurveys > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
                      <Smile className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Promotores</div>
                      <div className="text-2xl font-bold text-foreground">
                        {metrics.promoters} ({((metrics.promoters / metrics.totalSurveys) * 100).toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-yellow-500/10 rounded-full flex items-center justify-center">
                      <Meh className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Neutros</div>
                      <div className="text-2xl font-bold text-foreground">
                        {metrics.passives} ({((metrics.passives / metrics.totalSurveys) * 100).toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-red-500/10 rounded-full flex items-center justify-center">
                      <Frown className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Detratores</div>
                      <div className="text-2xl font-bold text-foreground">
                        {metrics.detractors} ({((metrics.detractors / metrics.totalSurveys) * 100).toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Delivery Timeline Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Linha do Tempo de Entregas (Últimos 6 Meses)</CardTitle>
        </CardHeader>
        <CardContent>
          {timeline.length > 0 ? (
            <div style={{ width: '100%', height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="deliveries" stroke="#3B82F6" name="Entregas" strokeWidth={2} />
                  {timeline.some((t) => t.avgNPS > 0) && (
                    <Line type="monotone" dataKey="avgNPS" stroke="#8B5CF6" name="NPS Médio" strokeWidth={2} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-muted-foreground">Nenhum dado disponível</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Deliveries */}
      <Card>
        <CardHeader>
          <CardTitle>Entregas Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {recentDeliveries.length > 0 ? (
            <div className="space-y-3">
              {recentDeliveries.map((delivery) => (
                <div key={delivery.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <p className="font-medium text-foreground">{delivery.customerName}</p>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{delivery.vehicleName}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Responsável: {delivery.responsibleName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-emerald-600">{delivery.status}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(delivery.deliveryDate)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Nenhuma entrega recente</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Placeholder for Future Features */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smile className="h-5 w-5 text-purple-600" />
              Pesquisas de Satisfação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-sm text-muted-foreground mb-2">Funcionalidade em desenvolvimento</p>
              <p className="text-xs text-muted-foreground text-center">
                Em breve você poderá enviar e gerenciar pesquisas de satisfação NPS
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Catálogo de Produtos/Serviços
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-sm text-muted-foreground mb-2">Funcionalidade em desenvolvimento</p>
              <p className="text-xs text-muted-foreground text-center">
                Em breve você poderá gerenciar o catálogo de produtos e serviços adicionais
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
